import { TooltipProvider } from "@/components/ui/tooltip";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  type LexicalEditor,
} from "lexical";
import { MessageSquare } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarFontSizeMenu,
  CanvasToolbarTypefaceMenu,
} from "../components/toolbar";
import {
  CanvasToolbarAlignMenu,
  CanvasToolbarFormatMenu,
} from "../components/toolbar/CanvasToolbarFormatMenu";
import {
  readCanvasAlignment,
  toggleCanvasStrikethrough,
} from "../components/toolbar/canvasFormatCommands";
import { applyCanvasListType } from "../components/toolbar/canvasListCommands";
import { LABEL_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasEmojiPicker } from "./shared/CanvasEmojiPicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

interface TextLabelFloatingToolbarProps {
  visible: boolean;
  /** Omit with `onTextColorChange` to hide the text color picker (e.g. shape labels). */
  textColor?: string;
  fontFamily?: string;
  fontSize?: string;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  onTextColorChange?: (color: string) => void;
  onFontFamilyChange: (fontFamily: string) => void;
  onFontSizeChange: (fontSize: string) => void;
  onBoldChange: () => void;
  onItalicChange?: () => void;
  onUnderlineChange?: () => void;
  onChatClick?: () => void;
  editor: LexicalEditor | null;
  onPopoverOpenChange?: (isOpen: boolean) => void;
  /** Formats that live in the content rather than on the node. */
  onContentFormatChange?: (patch: {
    is_strikethrough?: boolean;
    align?: "left" | "center" | "right";
  }) => void;
  /** Override the text color palette. Defaults to LABEL_COLORS. */
  textColors?: typeof LABEL_COLORS;
}

export function TextLabelFloatingToolbar({
  visible,
  textColor,
  fontFamily = "system-ui, -apple-system, sans-serif",
  fontSize = "16px",
  isBold = false,
  isItalic = false,
  isUnderline = false,
  onTextColorChange,
  onFontFamilyChange,
  onFontSizeChange,
  onBoldChange,
  onItalicChange,
  onUnderlineChange,
  onChatClick,
  editor,
  onPopoverOpenChange,
  onContentFormatChange,
  textColors = LABEL_COLORS,
}: TextLabelFloatingToolbarProps) {
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlignment, setTextAlignment] = useState<
    "left" | "center" | "right"
  >("left");

  // Refs updated synchronously before notify so parent latch never sees stale OR.
  const isFontFamilyOpenRef = useRef(false);
  const isFontSizeOpenRef = useRef(false);
  const isFormatOpenRef = useRef(false);
  const isAlignOpenRef = useRef(false);
  const isEmojiOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onPopoverOpenChange?.(
      isFontFamilyOpenRef.current ||
        isFontSizeOpenRef.current ||
        isFormatOpenRef.current ||
        isAlignOpenRef.current ||
        isEmojiOpenRef.current,
    );
  }, [onPopoverOpenChange]);

  const handleFontFamilyOpen = useCallback(
    (open: boolean) => {
      isFontFamilyOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFontSizeOpen = useCallback(
    (open: boolean) => {
      isFontSizeOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFormatOpen = useCallback(
    (open: boolean) => {
      isFormatOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleAlignOpen = useCallback(
    (open: boolean) => {
      isAlignOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleEmojiOpen = useCallback(
    (open: boolean) => {
      isEmojiOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  // The align indicator comes from the content on mount: a re-selected node
  // has no caret, so the update listener below never fires for it.
  useEffect(() => {
    setTextAlignment(readCanvasAlignment(editor));
  }, [editor]);

  // Track bullet-list state from the editor selection
  useEffect(() => {
    if (!editor) return;

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === "root"
              ? anchorNode
              : anchorNode.getTopLevelElementOrThrow();
          setIsStrikethrough(selection.hasFormat("strikethrough"));
          if ($isElementNode(element)) {
            const formatType = element.getFormatType();
            if (formatType === "center" || formatType === "right") {
              setTextAlignment(formatType);
            } else {
              setTextAlignment("left");
            }
          }
        }
      });
    });
  }, [editor]);

  const handleStrikethroughChange = useCallback(() => {
    toggleCanvasStrikethrough(editor);
    onContentFormatChange?.({ is_strikethrough: !isStrikethrough });
  }, [editor, isStrikethrough, onContentFormatChange]);

  const handleAlignChange = useCallback(
    (align: "left" | "center" | "right") => {
      editor?.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
      setTextAlignment(align);
      onContentFormatChange?.({ align });
    },
    [editor, onContentFormatChange],
  );

  if (!visible) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest("input")) return;
          e.preventDefault();
        }}
      >
        {/* Chat — accent purple, grouped first */}
        {onChatClick && (
          <CanvasToolbarButton
            label="Chat about this"
            accent
            onClick={(e) => {
              e.stopPropagation();
              onChatClick();
            }}
          >
            <MessageSquare className="size-4" />
          </CanvasToolbarButton>
        )}

        {onChatClick && <CanvasToolbarDivider />}

        <CanvasToolbarTypefaceMenu
          fontFamily={fontFamily}
          onFontFamilyChange={onFontFamilyChange}
          onOpenChange={handleFontFamilyOpen}
        />

        <CanvasToolbarFontSizeMenu
          fontSize={fontSize}
          onFontSizeChange={onFontSizeChange}
          onOpenChange={handleFontSizeOpen}
        />

        {/* Separator */}
        <CanvasToolbarDivider />

        <CanvasToolbarFormatMenu
          isBold={isBold}
          isItalic={isItalic}
          isUnderline={isUnderline}
          isStrikethrough={isStrikethrough}
          textColor={textColor}
          textColors={textColors}
          onBoldChange={onBoldChange}
          onItalicChange={onItalicChange}
          onUnderlineChange={onUnderlineChange}
          onStrikethroughChange={handleStrikethroughChange}
          onTextColorChange={onTextColorChange}
          onListTypeSelect={(type) => applyCanvasListType(editor, type)}
          onOpenChange={handleFormatOpen}
        />

        <CanvasToolbarAlignMenu
          alignment={textAlignment}
          onAlignChange={handleAlignChange}
          onOpenChange={handleAlignOpen}
        />

        {/* Emoji */}
        <CanvasEmojiPicker
          editor={editor}
          onPopoverOpenChange={handleEmojiOpen}
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
